"use client";

import React, { useMemo, useState } from "react";
import { BarChart, Card, DonutChart, Title, type EventProps } from "@tremor/react";
import { Empty, Skeleton } from "antd";
import dayjs from "dayjs";
import sortBy from "lodash.sortby";

import { getOrderRevenueBreakdown } from "@fitness-app/data-models/entities/Order";
import { type ProductPrice } from "@fitness-app/data-models/entities/Product";

import { useCheckoutVariantOrders } from "~/modules/Products/Product/ProductDetails/hooks/useCheckoutVariantOrders";
import { getPriceFormatter } from "~/modules/Products/utils/getPriceFormatter";

interface ProductCheckoutVariantStatsProps {
  checkoutVariantIds: string[];
  prices: ProductPrice[];
  productId: string;
}

const stackedChartColors = ["indigo", "cyan", "emerald", "amber", "red", "purple", "pink", "lime", "sky", "fuchsia"];

const getPriceLabel = (price: ProductPrice | undefined) => {
  if (!price) {
    return null;
  }

  if (price.customLabel) {
    return price.customLabel;
  }

  const promoConfig = price.config?.showAsPromo;
  if (promoConfig && typeof promoConfig === "object" && "promoTagText" in promoConfig && promoConfig.promoTagText) {
    return promoConfig.promoTagText;
  }

  if (price.unit_amount) {
    return getPriceFormatter(price.currency).format((price.unit_amount ?? 0) / 100);
  }

  return price.id;
};

const getPriceDisplayLabel = (price: ProductPrice | undefined, fallbackId: string) => {
  const label = getPriceLabel(price) ?? fallbackId;
  return `${label}${price?.currency ? ` (${price.currency.toUpperCase()})` : ""}`;
};

export const ProductCheckoutVariantStats = ({
  checkoutVariantIds,
  productId,
  prices,
}: ProductCheckoutVariantStatsProps): React.ReactElement => {
  const { data: paidOrders = [], isLoading } = useCheckoutVariantOrders(checkoutVariantIds, productId, null);
  const [donutActiveCategory, setDonutActiveCategory] = useState<string | null>(null);
  const [projectedDonutActiveCategory, setProjectedDonutActiveCategory] = useState<string | null>(null);

  const variantPrices = useMemo(() => {
    return prices.reduce<Record<string, ProductPrice>>((acc, price) => {
      acc[price.id] = price;
      return acc;
    }, {});
  }, [prices]);

  const revenuePerPrice = useMemo(() => {
    if (!paidOrders.length) {
      return { actual: [], projected: [] };
    }

    const aggregated = paidOrders.reduce<Record<string, { actual: number; projected: number; currency: string }>>(
      (acc, order) => {
        if (!order.priceId || !order.price || typeof order.price.amount !== "number") {
          return acc;
        }

        if (!variantPrices[order.priceId]) {
          return acc;
        }

        const currency = variantPrices[order.priceId]?.currency ?? "pln";
        const { paid, projected } = getOrderRevenueBreakdown(order);

        const current = acc[order.priceId];
        acc[order.priceId] = {
          actual: (current?.actual ?? 0) + paid / 100,
          projected: (current?.projected ?? 0) + projected / 100,
          currency,
        };
        return acc;
      },
      {},
    );

    const actual = sortBy(
      Object.entries(aggregated).map(([priceId, stats]) => {
        const price = variantPrices[priceId];
        const displayLabel = getPriceDisplayLabel(price, priceId);

        return {
          price: displayLabel,
          amount: stats.actual,
          currency: stats.currency,
        };
      }),
      (item) => -item.amount,
    );

    const projected = sortBy(
      Object.entries(aggregated).map(([priceId, stats]) => {
        const price = variantPrices[priceId];
        const displayLabel = getPriceDisplayLabel(price, priceId);

        return {
          price: displayLabel,
          amount: stats.projected,
          currency: stats.currency,
        };
      }),
      (item) => -item.amount,
    );

    return { actual, projected };
  }, [paidOrders, variantPrices]);

  const purchaseAmountPerPrice = revenuePerPrice.actual;
  const projectedAmountPerPrice = revenuePerPrice.projected;

  const purchaseAmountCurrency = purchaseAmountPerPrice[0]?.currency ?? "pln";
  const purchaseAmountValueFormatter = useMemo(
    () => getPriceFormatter(purchaseAmountCurrency),
    [purchaseAmountCurrency],
  );

  const projectedAmountCurrency = projectedAmountPerPrice[0]?.currency ?? purchaseAmountCurrency;
  const projectedAmountValueFormatter = useMemo(
    () => getPriceFormatter(projectedAmountCurrency),
    [projectedAmountCurrency],
  );

  const purchaseAmountCategories = useMemo(
    () => purchaseAmountPerPrice.map((item) => item.price),
    [purchaseAmountPerPrice],
  );

  const transactionsPerDay = useMemo(() => {
    if (!paidOrders.length) {
      return { data: [], categories: [] as string[] };
    }

    const aggregated = paidOrders.reduce<Record<string, Record<string, number>>>((acc, order) => {
      if (!order.createdAt || !order.priceId) {
        return acc;
      }

      const price = variantPrices[order.priceId];
      const displayLabel = getPriceDisplayLabel(price, order.priceId);
      const dateKey = dayjs(order.createdAt).format("YYYY-MM-DD");

      if (!acc[dateKey]) {
        acc[dateKey] = {};
      }

      acc[dateKey][displayLabel] = (acc[dateKey][displayLabel] ?? 0) + 1;
      return acc;
    }, {});

    const baseCategories = purchaseAmountCategories.length
      ? purchaseAmountCategories
      : Array.from(
          new Set(
            paidOrders.map((order) => getPriceDisplayLabel(variantPrices[order.priceId ?? ""], order.priceId ?? "")),
          ),
        );

    const data = sortBy(
      Object.entries(aggregated).map(([isoDate, counts]) => {
        const entry: Record<string, number | string> = {
          isoDate,
          date: dayjs(isoDate).format("DD.MM.YYYY"),
        };

        baseCategories.forEach((category) => {
          entry[category] = counts[category] ?? 0;
        });

        return entry as { isoDate: string; date: string } & Record<string, number>;
      }),
      "isoDate",
    );

    return {
      data,
      categories: baseCategories,
    };
  }, [paidOrders, purchaseAmountCategories, variantPrices]);

  const purchaseAmountPerDay = useMemo(() => {
    if (!paidOrders.length) {
      return {
        data: [],
        categories: [] as string[],
        currency: "pln",
      };
    }

    const categoriesSet = new Set<string>();
    let detectedCurrency: string | null = null;

    const aggregated = paidOrders.reduce<Record<string, Record<string, number>>>((acc, order) => {
      if (!order.createdAt || !order.priceId || !order.price || typeof order.price.amount !== "number") {
        return acc;
      }

      const price = variantPrices[order.priceId];
      if (!price) {
        return acc;
      }

      detectedCurrency = detectedCurrency ?? price.currency ?? null;
      const displayLabel = getPriceDisplayLabel(price, order.priceId);
      categoriesSet.add(displayLabel);

      const isoDate = dayjs(order.createdAt).format("YYYY-MM-DD");
      if (!acc[isoDate]) {
        acc[isoDate] = {};
      }
      acc[isoDate][displayLabel] = (acc[isoDate][displayLabel] ?? 0) + order.price.amount / 100;
      return acc;
    }, {});

    const baseCategories = purchaseAmountCategories.length ? purchaseAmountCategories : Array.from(categoriesSet);

    const data = sortBy(
      Object.entries(aggregated).map(([isoDate, amounts]) => {
        const entry: Record<string, number | string> = {
          isoDate,
          date: dayjs(isoDate).format("DD.MM.YYYY"),
        };

        baseCategories.forEach((category) => {
          entry[category] = amounts[category] ?? 0;
        });

        return entry as { isoDate: string; date: string } & Record<string, number>;
      }),
      "isoDate",
    );

    return {
      data,
      categories: baseCategories,
      currency: detectedCurrency ?? purchaseAmountCurrency,
    };
  }, [paidOrders, variantPrices, purchaseAmountCategories, purchaseAmountCurrency]);

  const purchaseAmountPerDayValueFormatter = useMemo(
    () => getPriceFormatter(purchaseAmountPerDay.currency ?? "pln"),
    [purchaseAmountPerDay.currency],
  );

  const donutChartData = useMemo(
    () =>
      purchaseAmountPerPrice.map((item) => ({
        name: item.price,
        amount: item.amount,
      })),
    [purchaseAmountPerPrice],
  );

  const donutChartTotalValue = useMemo(
    () => donutChartData.reduce((sum, item) => sum + item.amount, 0),
    [donutChartData],
  );

  const donutActiveValue = donutActiveCategory
    ? donutChartData.find((item) => item.name === donutActiveCategory)?.amount
    : null;

  const donutDisplayedAmount = typeof donutActiveValue === "number" ? donutActiveValue : donutChartTotalValue;
  const donutDisplayedLabel = donutActiveCategory ?? "Łącznie";

  const handleDonutValueChange = (value: EventProps) => {
    if (!value || typeof value !== "object") {
      setDonutActiveCategory(null);
      return;
    }

    const category = "categoryClicked" in value ? value.categoryClicked : null;
    setDonutActiveCategory(category ?? null);
  };

  const projectedDonutChartData = useMemo(
    () =>
      projectedAmountPerPrice.map((item) => ({
        name: item.price,
        amount: item.amount,
      })),
    [projectedAmountPerPrice],
  );

  const projectedDonutChartTotalValue = useMemo(
    () => projectedDonutChartData.reduce((sum, item) => sum + item.amount, 0),
    [projectedDonutChartData],
  );

  const projectedDonutActiveValue = projectedDonutActiveCategory
    ? projectedDonutChartData.find((item) => item.name === projectedDonutActiveCategory)?.amount
    : null;

  const projectedDonutDisplayedAmount =
    typeof projectedDonutActiveValue === "number" ? projectedDonutActiveValue : projectedDonutChartTotalValue;
  const projectedDonutDisplayedLabel = projectedDonutActiveCategory ?? "Łącznie";

  const handleProjectedDonutValueChange = (value: EventProps) => {
    if (!value || typeof value !== "object") {
      setProjectedDonutActiveCategory(null);
      return;
    }

    const category = "categoryClicked" in value ? value.categoryClicked : null;
    setProjectedDonutActiveCategory(category ?? null);
  };

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 ">
        <Card>
          <Title>Przychód per cena</Title>
          {purchaseAmountPerPrice.length ? (
            <div className="flex flex-col items-center">
              <p className="text-center text-sm text-gray-700 dark:text-gray-300">{donutDisplayedLabel}</p>
              <p className="mt-2 text-center text-2xl font-semibold text-gray-900 dark:text-gray-50">
                {purchaseAmountValueFormatter.format(donutDisplayedAmount ?? 0)}
              </p>
              <DonutChart
                data={donutChartData}
                category="amount"
                index="name"
                className="mx-auto mt-8 h-64 w-64"
                colors={stackedChartColors}
                valueFormatter={(value) => purchaseAmountValueFormatter.format(value ?? 0)}
                showLabel={false}
                onValueChange={handleDonutValueChange}
              />
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
          )}
        </Card>

        <Card>
          <Title>Projektowany przychód</Title>
          {projectedAmountPerPrice.length ? (
            <div className="flex flex-col items-center">
              <p className="text-center text-sm text-gray-700 dark:text-gray-300">{projectedDonutDisplayedLabel}</p>
              <p className="mt-2 text-center text-2xl font-semibold text-gray-900 dark:text-gray-50">
                {projectedAmountValueFormatter.format(projectedDonutDisplayedAmount ?? 0)}
              </p>
              <DonutChart
                data={projectedDonutChartData}
                category="amount"
                index="name"
                className="mx-auto mt-8 h-64 w-64"
                colors={stackedChartColors}
                valueFormatter={(value) => projectedAmountValueFormatter.format(value ?? 0)}
                showLabel={false}
                onValueChange={handleProjectedDonutValueChange}
              />
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
          )}
        </Card>
      </div>

      <Card>
        <Title>Transakcje dziennie</Title>
        {transactionsPerDay.data.length ? (
          <BarChart
            className="mt-6 h-72"
            data={transactionsPerDay.data}
            index="date"
            categories={transactionsPerDay.categories}
            colors={stackedChartColors}
            valueFormatter={(value) => (value != null ? Intl.NumberFormat("pl-PL").format(value) : "0")}
            showLegend={transactionsPerDay.categories.length > 1}
            allowDecimals={false}
            stack={true}
            showGridLines={true}
            showYAxis={true}
            yAxisWidth={80}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
        )}
      </Card>

      <Card>
        <Title>Przychód dziennie (wg ceny)</Title>
        {purchaseAmountPerDay.data.length ? (
          <BarChart
            className="mt-6 h-80"
            data={purchaseAmountPerDay.data}
            index="date"
            categories={purchaseAmountPerDay.categories}
            colors={stackedChartColors}
            valueFormatter={(value) => purchaseAmountPerDayValueFormatter.format(value ?? 0)}
            showLegend={true}
            allowDecimals={false}
            showGridLines={true}
            showYAxis={true}
            yAxisWidth={100}
            stack={true}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
        )}
      </Card>
    </div>
  );
};
